import { Link } from "@fluentui/react-components";
import { ExternalLayout } from "layouts/externalLayout/externalLayout";
import { useStyles } from "./AcceptableUsePolicyPage.styles";

const LAST_UPDATED = "June 8, 2026";
const SUPPORT_EMAIL = "techenablement@northhighland.com";

export const AcceptableUsePolicyPage = (): JSX.Element => {
  const styles = useStyles();

  return (
    <ExternalLayout hideHelp>
      <div className={styles.page}>
        <a href="#aup-start" className={styles.skipLink}>
          Skip to policy content
        </a>

        <header aria-label="Page header" className={styles.header}>
          <h1 className={styles.title}>Project IQ Acceptable Use Policy</h1>
          <span className={styles.updated} aria-live="polite">
            Last updated: <time dateTime="2026-06-08">{LAST_UPDATED}</time>
          </span>
        </header>

        <nav aria-label="On this page" className={styles.toc}>
          <p className={styles.tocHeading}>On this page</p>
          <ul className={styles.tocList}>
            <li>
              <Link className={styles.tocLink} href="#purpose">
                Purpose
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#scope">
                Scope
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#acceptable-use">
                Acceptable Use
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#prohibited-use">
                Prohibited Use
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#data-privacy">
                Data Privacy &amp; Security
              </Link>
            </li>
            <li>
              <Link
                className={styles.tocLink}
                href="#professional-responsibility"
              >
                Professional Responsibility
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#compliance">
                Compliance &amp; Legal
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#monitoring">
                Monitoring &amp; Enforcement
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#support">
                Support &amp; Questions
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#acknowledgment">
                Acknowledgment
              </Link>
            </li>
            <li>
              <Link className={styles.tocLink} href="#policy-updates">
                Policy Updates
              </Link>
            </li>
          </ul>
        </nav>

        <div id="aup-start" />

        <section id="purpose" className={styles.section}>
          <h2 className={styles.sectionTitle}>Purpose</h2>
          <p className={styles.paragraph}>
            This Acceptable Use Policy governs the use of North Highland&apos;s
            AI Management System (Project IQ), including all features such as
            chat functionality, Workspaces, AI Interviews, AI Analytics,
            Knowledge Components, and Use Case Prompts. Project IQ is designed
            to enhance productivity, support client engagements, and maintain
            North Highland&apos;s standards of excellence while ensuring
            responsible AI use.
          </p>
        </section>

        <section id="scope" className={styles.section}>
          <h2 className={styles.sectionTitle}>Scope</h2>
          <p className={styles.paragraph}>
            This policy applies to all North Highland employees, contractors,
            and authorized users accessing Project IQ in any capacity.
          </p>
        </section>

        <section id="acceptable-use" className={styles.section}>
          <h2 className={styles.sectionTitle}>Acceptable Use</h2>

          <h3 className={styles.subSectionTitle}>General Principles</h3>
          <p className={styles.paragraph}>Users are expected to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Use Project IQ for legitimate business purposes related to North
              Highland work, client engagements, proposals, research, and
              professional development
            </li>
            <li className={styles.listItem}>
              Exercise professional judgment when using AI-generated content
            </li>
            <li className={styles.listItem}>
              Validate and verify all AI outputs before using them in client
              deliverables, proposals, or business decisions
            </li>
            <li className={styles.listItem}>
              Cite sources appropriately and never present AI-generated content
              as original work without review and customization
            </li>
            <li className={styles.listItem}>
              Respect intellectual property rights and North Highland&apos;s
              proprietary information
            </li>
            <li className={styles.listItem}>
              Maintain accuracy by fact-checking AI responses against
              authoritative sources
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Workspaces</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Create Workspaces for specific client engagements, projects, or
              topics to organize conversations and documents
            </li>
            <li className={styles.listItem}>
              Add only authorized team members who have a legitimate business
              need to access the Workspace
            </li>
            <li className={styles.listItem}>
              Use appropriate naming conventions that clearly identify the
              purpose without exposing confidential information in titles
            </li>
            <li className={styles.listItem}>
              Regularly review and archive completed Workspaces
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>AI Interviews</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Obtain appropriate approvals before deploying AI Interviews to
              external stakeholders or clients
            </li>
            <li className={styles.listItem}>
              Clearly communicate to participants how their data will be used
              and stored
            </li>
            <li className={styles.listItem}>
              Use AI Interviews only for authorized research, diagnostics, and
              stakeholder engagement activities
            </li>
            <li className={styles.listItem}>
              Review and validate AI-generated interview questions before
              distribution
            </li>
            <li className={styles.listItem}>
              Protect participant confidentiality and handle responses according
              to data privacy requirements
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>AI Analytics</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Upload only data you are authorized to analyze
            </li>
            <li className={styles.listItem}>
              Ensure datasets do not contain personally identifiable information
              (PII) unless explicitly approved and necessary
            </li>
            <li className={styles.listItem}>
              Validate AI-generated insights before including them in client
              deliverables
            </li>
            <li className={styles.listItem}>
              Use analytics results to inform, not replace, professional
              judgment
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>
            Knowledge Components &amp; Prompts
          </h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Use Knowledge Components to provide relevant context for more
              accurate AI responses
            </li>
            <li className={styles.listItem}>
              Do not upload client confidential information to Knowledge
              Components without proper authorization
            </li>
            <li className={styles.listItem}>
              Leverage Use Case Prompts as starting points, but customize
              outputs to meet specific needs
            </li>
            <li className={styles.listItem}>
              Share useful prompts with colleagues to build organizational
              knowledge
            </li>
          </ul>
        </section>

        <section id="prohibited-use" className={styles.section}>
          <h2 className={styles.sectionTitle}>Prohibited Use</h2>
          <p className={styles.paragraph}>Users must NOT:</p>

          <h3 className={styles.subSectionTitle}>Data &amp; Confidentiality</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Upload, share, or process client confidential information without
              explicit authorization and appropriate safeguards
            </li>
            <li className={styles.listItem}>
              Include personally identifiable information (PII), protected
              health information (PHI), or sensitive personal data unless
              specifically authorized and necessary
            </li>
            <li className={styles.listItem}>
              Share proprietary North Highland methodologies, tools, or
              intellectual property outside authorized channels
            </li>
            <li className={styles.listItem}>
              Use Project IQ to store or transmit information that violates
              client contracts, NDAs, or data protection agreements
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Content &amp; Output</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Present AI-generated content as final work without human review,
              validation, and customization
            </li>
            <li className={styles.listItem}>
              Use Project IQ to create misleading, false, or deceptive content
            </li>
            <li className={styles.listItem}>
              Generate content that is discriminatory, harassing, offensive, or
              inappropriate for a professional environment
            </li>
            <li className={styles.listItem}>
              Plagiarize or violate copyright by using AI to reproduce protected
              works without authorization
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>System Integrity</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Attempt to circumvent security controls, access restrictions, or
              usage monitoring
            </li>
            <li className={styles.listItem}>
              Share login credentials or provide unauthorized access to Project
              IQ
            </li>
            <li className={styles.listItem}>
              Use Project IQ in ways that could compromise system performance or
              availability for other users
            </li>
            <li className={styles.listItem}>
              Attempt to reverse engineer, modify, or exploit Project IQ
              functionality
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Misuse</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Use Project IQ for personal projects, side businesses, or
              non-North Highland work
            </li>
            <li className={styles.listItem}>
              Generate content for competitors or conflicting business interests
            </li>
            <li className={styles.listItem}>
              Use Project IQ to automate spam, mass communications, or
              unsolicited outreach
            </li>
            <li className={styles.listItem}>
              Engage in any illegal, unethical, or fraudulent activities
            </li>
          </ul>
        </section>

        <section id="data-privacy" className={styles.section}>
          <h2 className={styles.sectionTitle}>Data Privacy &amp; Security</h2>

          <h3 className={styles.subSectionTitle}>Client Data</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Client confidential information may only be used in Project IQ
              when:
              <ul className={styles.nestedList}>
                <li className={styles.listItem}>
                  You have explicit authorization to do so
                </li>
                <li className={styles.listItem}>
                  The engagement or project requires AI assistance
                </li>
                <li className={styles.listItem}>
                  Appropriate data handling protocols are followed
                </li>
                <li className={styles.listItem}>
                  Client contracts permit the use of AI tools
                </li>
              </ul>
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Data Handling</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Project IQ conversations and uploads are logged and may be
              monitored for security, compliance, and quality purposes
            </li>
            <li className={styles.listItem}>
              Do not assume privacy for any content entered into Project IQ
            </li>
            <li className={styles.listItem}>
              Workspace content is accessible to all designated collaborators
            </li>
            <li className={styles.listItem}>
              Follow North Highland&apos;s data classification and handling
              standards
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Retention</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Workspace content and conversation history are retained according
              to North Highland&apos;s data retention policies
            </li>
            <li className={styles.listItem}>
              Users are responsible for archiving or deleting Workspaces when
              projects conclude
            </li>
            <li className={styles.listItem}>
              Exported data from AI Interviews and AI Analytics must be handled
              according to engagement-specific data management plans
            </li>
          </ul>
        </section>

        <section id="professional-responsibility" className={styles.section}>
          <h2 className={styles.sectionTitle}>Professional Responsibility</h2>

          <h3 className={styles.subSectionTitle}>Validation &amp; Accuracy</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Always verify AI-generated information before using it in client
              work, proposals, or business decisions
            </li>
            <li className={styles.listItem}>
              Cross-reference AI outputs with authoritative sources, subject
              matter experts, and established methodologies
            </li>
            <li className={styles.listItem}>
              Recognize that AI can produce plausible but incorrect information
              (&quot;hallucinations&quot;)
            </li>
            <li className={styles.listItem}>
              Apply critical thinking and professional expertise to all
              AI-assisted work
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Quality Standards</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              AI-generated content must meet North Highland&apos;s quality
              standards before client delivery
            </li>
            <li className={styles.listItem}>
              Customize and refine AI outputs to reflect North Highland&apos;s
              tone, voice, and brand standards
            </li>
            <li className={styles.listItem}>
              Ensure all deliverables demonstrate original thinking and
              professional judgment
            </li>
            <li className={styles.listItem}>
              Use AI as a tool to enhance, not replace, human expertise
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Transparency</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Be transparent with clients when AI tools have been used to
              support engagement work (when appropriate and required)
            </li>
            <li className={styles.listItem}>
              Clearly distinguish between AI-generated drafts and finalized,
              expert-reviewed deliverables
            </li>
            <li className={styles.listItem}>
              Disclose AI use when required by client contracts or engagement
              terms
            </li>
          </ul>
        </section>

        <section id="compliance" className={styles.section}>
          <h2 className={styles.sectionTitle}>Compliance &amp; Legal</h2>
          <p className={styles.paragraph}>Users must comply with:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              All applicable data protection laws (GDPR, CCPA, etc.)
            </li>
            <li className={styles.listItem}>
              Client contracts and NDAs regarding data use and AI tools
            </li>
            <li className={styles.listItem}>
              North Highland&apos;s Information Security Policy, AI Governance
              Policies and related standards
            </li>
            <li className={styles.listItem}>
              Export control regulations when working with international data
            </li>
            <li className={styles.listItem}>
              Industry-specific regulations (HIPAA, financial services
              regulations, etc.) as applicable
            </li>
          </ul>
        </section>

        <section id="monitoring" className={styles.section}>
          <h2 className={styles.sectionTitle}>Monitoring &amp; Enforcement</h2>

          <h3 className={styles.subSectionTitle}>Monitoring</h3>
          <p className={styles.paragraph}>
            North Highland reserves the right to:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Monitor Project IQ usage for compliance, security, and quality
              assurance
            </li>
            <li className={styles.listItem}>
              Review Workspace content, conversation history, and uploaded
              documents
            </li>
            <li className={styles.listItem}>
              Audit AI Interview deployments and AI Analytics projects
            </li>
            <li className={styles.listItem}>
              Investigate suspected policy violations
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Violations</h3>
          <p className={styles.paragraph}>
            Violations of this policy may result in:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Immediate suspension of Project IQ access
            </li>
            <li className={styles.listItem}>
              Disciplinary action up to and including termination
            </li>
            <li className={styles.listItem}>
              Legal action if violations involve illegal activity or breach of
              contract
            </li>
            <li className={styles.listItem}>
              Notification to affected clients if confidentiality or data
              protection is compromised
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Reporting</h3>
          <p className={styles.paragraph}>
            Users who observe or suspect policy violations should report them
            to:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Their direct manager</li>
            <li className={styles.listItem}>
              North Highland&apos;s Information Security team
            </li>
            <li className={styles.listItem}>
              The Ethics Hotline (for serious violations)
            </li>
          </ul>
        </section>

        <section id="support" className={styles.section}>
          <h2 className={styles.sectionTitle}>Support &amp; Questions</h2>

          <h3 className={styles.subSectionTitle}>Getting Help</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <strong>Policy Questions:</strong> Reach out to{" "}
              <Link className={styles.link} href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </Link>
            </li>
            <li className={styles.listItem}>
              <strong>Data Privacy Concerns:</strong> Contact the Information
              Security team
            </li>
            <li className={styles.listItem}>
              <strong>Client-Specific Guidance:</strong> Consult your engagement
              leader or account manager
            </li>
          </ul>

          <h3 className={styles.subSectionTitle}>Resources</h3>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Project IQ User Guides and training materials: Available on the
              Hub
            </li>
            <li className={styles.listItem}>
              Knowledge Management team: For questions about Knowledge
              Components
            </li>
            <li className={styles.listItem}>
              Project IQ support:{" "}
              <Link className={styles.link} href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </Link>
            </li>
          </ul>
        </section>

        <section id="acknowledgment" className={styles.section}>
          <h2 className={styles.sectionTitle}>Acknowledgment</h2>
          <p className={styles.paragraph}>
            By using Project IQ, you acknowledge that you have read, understood,
            and agree to comply with this Acceptable Use Policy. You understand
            that violations may result in disciplinary action and that North
            Highland reserves the right to modify this policy at any time.
          </p>
        </section>

        <section id="policy-updates" className={styles.section}>
          <h2 className={styles.sectionTitle}>Policy Updates</h2>
          <p className={styles.paragraph}>
            This policy will be reviewed and updated periodically to reflect
            changes in technology, regulations, and business needs. Users will
            be notified of significant changes through standard North Highland
            communication channels.
          </p>
          <p className={styles.paragraph}>
            Questions about this policy? Contact{" "}
            <Link className={styles.link} href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </Link>
          </p>
          <p className={styles.footnote}>
            This policy is part of North Highland&apos;s broader Information
            Security and Technology Use policies. In case of conflict, the most
            restrictive policy applies.
          </p>
        </section>
      </div>
    </ExternalLayout>
  );
};
